import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../../Components/Layout";
import { useNavigate, useParams } from "react-router-dom";
import ProTextInput from "../../../Components/Inputs/TextField";
import { useForm, useWatch } from "react-hook-form";
import { Grid } from "@mui/material";
import SettingFormsLayout from "../../../Components/ProForms/ProFormsLayout/SettingFormsLayout";
import { typeSettingConfig } from "./typeSettingConfig";
import { DETAULT_ROLE, EMPLOYEE_ROLE, STAUTES } from "../../../../utils/constant";
import { getApi, postApi, deleteApi } from "../../../../utils/services";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_SETTINGS } from "../../../../utils/permissionReferenceConfig";
import usePermission from "../../../../utils/customHooks/usePermission";
import { useTheme } from "../../../ContextStore/ThemeContext";
import UserRoleAccessPicker from "./userRoleAccessPicker";
import { ALL_PROJECT_ACCESS } from "../../../../utils/services/apiPath";
import ToggleField from "../../../Components/Inputs/ToggleButton";
import { fieldTruePermission } from "../../../../utils/formHelper";
import { useFeatureFlagEnabled } from "posthog-js/react";


export default function TypeSettingForm() {
  const { id, settingType } = useParams();
  const navigate = useNavigate();
  const isAddMode = useMemo(() => id === "add", [id]);
  const [isSaving, setIsSaving] = useState(false);
  const isRolesPage = settingType === "roles";
  const { theme } = useTheme()

  const [typeData, setTypeData] = useState({});
  const [mode, setMode] = useState(isAddMode ? "edit" : "read");
  const [status, setStatus] = useState(STAUTES.IDLE);

  const { handleSubmit, control, setValue, getValues } = useForm({ defaultValues: {accessAllProjects: false} });
  const accessAllProjects = useWatch({ control, name: "accessAllProjects" });

  const isProjectAccessFeatureEnabled = useFeatureFlagEnabled("is_user_role_project_access_enabled");
  const showProjectAccess = isRolesPage && isProjectAccessFeatureEnabled;

  const {
    idKey,
    perKey,
    typeKey,
    typeLabel,
    getByIdApiPath,
    addApiPath,
    subModuleRef,
    addFormName,
    updateApiPath,
    getWithPostMethod = true
  } = useMemo(() => typeSettingConfig[settingType] || {}, [settingType]);
  const { [addFormName]: formPermission } = usePermission(subModPermissionSelector(R_SETTINGS, subModuleRef), addFormName);
  const { canView, canAdd, fieldPerObj } = formPermission;
  const backAction = () => navigate(`/settings/${settingType}`);

  const getDetailById = useCallback(
    async id => {
      setStatus(STAUTES.LOADING);
      const apiConfig = getWithPostMethod ? [postApi, getByIdApiPath, { [idKey]: id }] : [getApi, `${getByIdApiPath}/${id}`, {}];
      const { data, error } = await apiConfig[0](...apiConfig.slice(1));
      setStatus(STAUTES.IDLE);
      if (error) return errorToast(error);
      setTypeData(data);
      setValue(typeKey, data[typeKey]);
      setValue("accessAllProjects", data?.isAllProjectsAccess ?? true, { shouldDirty: false });
    },
    [setValue, getWithPostMethod, typeKey, getByIdApiPath, idKey]
  );


const GUID_RE = /[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}/i;

const getFirstGuid = (val) => {
  if (val == null) return null;
  if (typeof val === "string") {
    const m = val.match(GUID_RE);
    return m ? m[0] : null;
  }
  try {
    const s = typeof val === "object" ? JSON.stringify(val) : String(val);
    const m = s.match(GUID_RE);
    return m ? m[0] : null;
  } catch {
    return null;
  }
};

const extractRoleTypeIdFromAddResponse = (res) => {
  const direct =
    getFirstGuid(res?.data?.roleTypeId) || getFirstGuid(res?.data?.roleTypeGuid) || getFirstGuid(res?.data?.id);
  if (direct) return direct;
  return (
    getFirstGuid(res?.data) || getFirstGuid(res?.response?.data) || getFirstGuid(res?.result) || getFirstGuid(res?.payload) || getFirstGuid(res?.message) || getFirstGuid(res)
  );
};


const postProjectAccess = async (roleTypeId, projectIds) => {
  const ids = Array.isArray(projectIds) ? projectIds.filter(Boolean) : [];
  if (!roleTypeId || ids.length === 0) return;
  const { error } = await postApi(ALL_PROJECT_ACCESS, { roleTypeId, projectIds: ids });
  if (error) throw new Error(error);
};

const deleteProjectAccess = async (roleTypeId, projectIds) => {
  const ids = Array.isArray(projectIds) ? projectIds.filter(Boolean) : [];
  if (!roleTypeId || ids.length === 0) return;
  const { error } = await deleteApi(ALL_PROJECT_ACCESS, { roleTypeId, projectIds: ids });
  if (error) throw new Error(error);
};

const diffProjectIds = (prevIds = [], nextIds = []) => {
  const prev = new Set(prevIds.filter(Boolean));
  const next = new Set(nextIds.filter(Boolean));
  const added = [...next].filter(id => !prev.has(id));
  const removed = [...prev].filter(id => !next.has(id));
  return { added, removed };
};

const onSubmit = async (formData) => {
  if (isSaving) return;

  setIsSaving(true);
  setStatus(STAUTES.LOADING);

  try {
    const isRolesPage = settingType === "roles" || typeKey === "roleTypeName";
    const { userProjectAccessIds = [], accessAllProjects, ...rolePayload } = { ...typeData, ...formData };
    const apiPayload = {
     ...rolePayload,
     isAllProjectsAccess: Boolean(accessAllProjects),
   };
    //const res = await postApi(isAddMode ? addApiPath : updateApiPath, rolePayload);
    const res = await postApi(isAddMode ? addApiPath : updateApiPath, apiPayload);
    if (res?.error) {
      errorToast(res.error);
      return;
    }

    successToast(`${typeLabel} ${isAddMode ? "added" : "updated"} successfully.`);

    if (isRolesPage) {
      const roleTypeId = isAddMode ? extractRoleTypeIdFromAddResponse(res) : id;

      if (showProjectAccess) {
        if (!roleTypeId) {
          errorToast("Could not determine Role ID to save project access.");
        } else {
          if (!accessAllProjects) {
            const { added, removed } = diffProjectIds(initialProjectSelected, userProjectAccessIds);
            const ops = [];

            if (added.length) {
              ops.push(postProjectAccess(roleTypeId, added));
            }

            if (!isAddMode && removed.length) {
              ops.push(deleteProjectAccess(roleTypeId, removed));
            }
            
            if (ops.length) {
              await Promise.all(ops);
              const roleName = rolePayload?.[typeKey] ?? formData?.[typeKey] ?? "";
              if (added.length)  successToast(`Granted access to ${added.length} project(s)${roleName ? ` for "${roleName}"` : ""}.`);
              if (!isAddMode && removed.length) successToast(`Revoked access from ${removed.length} project(s)${roleName ? ` for "${roleName}"` : ""}.`);
            }
          }
        }
      }
    }

    backAction();
  } catch (e) {
    console.error("Save failed:", e);
    errorToast(e?.message ?? "Failed to save.");
  } finally {
    setIsSaving(false);
    setStatus(STAUTES.IDLE);
  }
};

const onEdit = () => setMode("edit");

const onCancel = () => {
  if (isAddMode) backAction();
  else setMode("read");
};

const [projectAccessOptions, setProjectAccessOptions] = useState([]);
const [initialProjectSelected, setInitialProjectSelected] = useState([]);
const [projectAccessLoading, setProjectAccessLoading] = useState(false);

const mapAndSetProjectAccess = useCallback((payload) => {
  const denied  = Array.isArray(payload?.deniedProjects)  ? payload.deniedProjects  : [];
  const granted = Array.isArray(payload?.grantedProjects) ? payload.grantedProjects : [];

  const byId = new Map(
    [...denied, ...granted]
      .filter(p => p?.projectId)
      .map(p => [p.projectId, { accessId: p.projectId, accessName: p.projectName ?? "" }])
  );

  const options = Array.from(byId.values());
  const initiallyGrantedIds = granted.map(p => p.projectId);

  setProjectAccessOptions(options);
  setInitialProjectSelected(initiallyGrantedIds);

  setValue("userProjectAccessIds", initiallyGrantedIds, { shouldDirty: false });
}, [setValue]);


const fetchProjectAccessForAdd = useCallback(async () => {
  try {
    setProjectAccessLoading(true);
    const res = await getApi(ALL_PROJECT_ACCESS, {});
    setProjectAccessLoading(false);

    if (res?.error) return errorToast(res.error);

    const payload = res?.data ?? res?.response?.data ?? res?.result ?? res?.payload ?? res;
    if (!payload || typeof payload !== "object") return errorToast("Unexpected response while loading project access.");

    mapAndSetProjectAccess(payload);
  } catch (e) {
    setProjectAccessLoading(false);
    console.error("[ProjectAccess][ADD] fetch failed:", e);
    errorToast("Failed to load project access.");
  }
}, [mapAndSetProjectAccess]);

const fetchProjectAccessForEdit = useCallback(async (roleGuid) => {
  try {
    setProjectAccessLoading(true);
    const res = await getApi(`${ALL_PROJECT_ACCESS}/${roleGuid}`, {});
    setProjectAccessLoading(false);

    if (res?.error) return errorToast(res.error);

    const payload = res?.data ?? res?.response?.data ?? res?.result ?? res?.payload ?? res;
    if (!payload || typeof payload !== "object") return errorToast("Unexpected response while loading project access.");

    mapAndSetProjectAccess(payload);
  } catch (e) {
    setProjectAccessLoading(false);
    errorToast("Failed to load project access.");
  }
}, [mapAndSetProjectAccess]);



  useEffect(() => {
    if (!typeKey) navigate("/");
  }, [typeKey, navigate]);

  useEffect(() => {
    if (!isAddMode) getDetailById(id);
  }, [id, isAddMode, getDetailById]);

  useEffect(() => {
    if (!showProjectAccess) return;

    if (isAddMode) {
      fetchProjectAccessForAdd();
    } else if (id) {
      fetchProjectAccessForEdit(id);
    }
  }, [isRolesPage, isAddMode, id, fetchProjectAccessForAdd, fetchProjectAccessForEdit]);

  if (!typeKey) return null;
  const hideEdit = typeKey === "roleTypeName" && (typeData[typeKey] === DETAULT_ROLE || typeData[typeKey] === EMPLOYEE_ROLE);
  return (
    <Layout status={status} canView={canView && !(isAddMode && !canAdd)}>
      <SettingFormsLayout
        backAction={backAction}
        title={`${isAddMode ? "Add" : "Edit"} ${typeLabel}`}
        backLabel={`Go Back to All ${typeLabel}`}
        beingEdited={mode === "edit"}
        onEdit={onEdit}
        onCancel={onCancel}
        onSave={handleSubmit(onSubmit)}
        hideEdit={hideEdit}
        isSaving={isSaving}
      >
        <Grid container spacing={10}>
          <ProTextInput
            name={typeKey}
            label={typeLabel}
            placeholder={typeLabel}
            control={control}
            mode={mode}
            isAddMode={isAddMode}
            formValues={typeData}
            rules={{ required: `${typeLabel} is required!` }}
            handleReadValueClick={hideEdit ? null : onEdit}
            permission={fieldPerObj[perKey]}
          />
          {showProjectAccess && (
            <ToggleField label="Access all projects" name="accessAllProjects" defaultValue={false} control={control} formValues={typeData} handleReadValueClick={hideEdit ? null : onEdit} permission={{ canView: true, canEdit: mode === "edit" }}/>
          )}
        </Grid>

        {showProjectAccess && (
          <UserRoleAccessPicker
            title="Project Access"
            userAccessList={projectAccessOptions}
            theme={theme}
            control={control}
            setValue={setValue}
            getValues={getValues}
            formFieldName="userProjectAccessIds"
            initialSelectedIds={initialProjectSelected}
            disabled={mode !== "edit" || projectAccessLoading || accessAllProjects}
          />
        )}
      </SettingFormsLayout>
    </Layout>
  );
}